import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  fetchSavedReports,
  fetchSavedReport,
  createSavedReport,
  updateSavedReport,
  deleteSavedReport,
  duplicateSavedReport,
} from '@/utils/savedReportsAPI';
import type {
  SavedReportWithMeta,
  CreateSavedReportRequest,
  UpdateSavedReportRequest,
} from '@/types/savedReports';

const QUERY_KEY = 'savedReports';

/**
 * Hook to fetch all accessible saved reports
 */
export function useSavedReports() {
  return useQuery<SavedReportWithMeta[], Error>({
    queryKey: [QUERY_KEY],
    queryFn: () => fetchSavedReports(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Hook to fetch a single saved report by ID
 */
export function useSavedReport(id: string | null) {
  return useQuery<SavedReportWithMeta | null, Error>({
    queryKey: [QUERY_KEY, id],
    queryFn: () => id ? fetchSavedReport(id) : Promise.resolve(null),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

/**
 * Hook to create a new saved report
 */
export function useCreateSavedReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateSavedReportRequest) => createSavedReport(request),
    onSuccess: (savedReport) => {
      // Get current user to determine isOwner
      const isOwner = true; // New reports are always owned by current user

      // Optimistic update: add to cache instead of invalidating
      queryClient.setQueryData([QUERY_KEY, savedReport.id], {
        ...savedReport,
        isOwner,
      });

      // Add to list cache
      queryClient.setQueryData([QUERY_KEY], (old: any[] | undefined) => {
        if (!old) return [{ ...savedReport, isOwner }];
        return [{ ...savedReport, isOwner }, ...old];
      });

      toast.success(`Report "${savedReport.name}" saved`);
    },
    onError: (error: Error) => {
      toast.error(`Failed to save report: ${error.message}`);
    },
  });
}

/**
 * Hook to update an existing saved report
 */
export function useUpdateSavedReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: UpdateSavedReportRequest }) =>
      updateSavedReport(id, request),
    onSuccess: (savedReport) => {
      // Update caches with server response
      queryClient.setQueryData([QUERY_KEY, savedReport.id], (old: any) => ({
        ...old,
        ...savedReport,
        isOwner: old?.isOwner ?? true,
      }));

      queryClient.setQueryData([QUERY_KEY], (old: any[] | undefined) => {
        if (!old) return old;
        return old.map(report =>
          report.id === savedReport.id
            ? { ...report, ...savedReport }
            : report
        );
      });

      toast.success('Report updated');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update report: ${error.message}`);
    },
  });
}

/**
 * Hook to delete a saved report
 */
export function useDeleteSavedReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteSavedReport(id),
    onSuccess: (_data, id) => {
      // Optimistic update: remove from cache
      queryClient.removeQueries({ queryKey: [QUERY_KEY, id] });

      // Remove from list cache
      queryClient.setQueryData([QUERY_KEY], (old: any[] | undefined) => {
        if (!old) return old;
        return old.filter(report => report.id !== id);
      });

      toast.success('Report deleted');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete report: ${error.message}`);
    },
  });
}

/**
 * Hook to duplicate a saved report as a personal copy
 */
export function useDuplicateSavedReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, newName }: { id: string; newName: string }) =>
      duplicateSavedReport(id, newName),
    onSuccess: (savedReport) => {
      const isOwner = true; // Duplicated reports are always owned by current user

      // Optimistic update: add to cache
      queryClient.setQueryData([QUERY_KEY, savedReport.id], {
        ...savedReport,
        isOwner,
      });

      // Add to list cache at the top
      queryClient.setQueryData([QUERY_KEY], (old: any[] | undefined) => {
        if (!old) return [{ ...savedReport, isOwner }];
        return [{ ...savedReport, isOwner }, ...old];
      });

      toast.success(`Report duplicated as "${savedReport.name}"`);
    },
    onError: (error: Error) => {
      toast.error(`Failed to duplicate report: ${error.message}`);
    },
  });
}
